/**
 * DownloadsTab.tsx
 *
 * PLAT-10014: Downloads tab in Clip Preview. Flat list of all PublishRecords
 * for the current AR's video, ordered newest-first. One status/action column
 * per row: in-flight rows show the status badge, succeeded rows swap it for
 * share-live-link + download actions, failed rows show the badge + error text.
 * Shares status/download presentation with the Export workflow
 * (`publishStatusUi` / `usePublishRecordDownload`).
 */

import { selectedRowIndicator } from '@src/components/common/selectionStyles';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import DownloadIcon from '@src/svgs/DownloadIcon';
import GlobeExternalIcon from '@src/svgs/GlobeExternalIcon';
import { PublishStatus } from '@src/types.ts/publishRecord';
import { formatDateTime } from '@src/utility/dateFormat';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { isOutdated, whyOutdated } from '@src/utility/publishFingerprints';
import { formatResolutionShortLabel } from '@src/utility/videoResolution';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { findClipForVideoId, shareLanguageForRecord } from './clipLink';
import ClipShareUnlistedLinkModal from './ClipShareUnlistedLinkModal';
import { useClipPublishRecords } from './hooks/useClipPublishRecords';
import { useCurrentPublishFingerprints } from './hooks/useCurrentPublishFingerprints';
import { usePublishRecordDownload } from './hooks/usePublishRecordDownload';
import {
  CurrentBadge,
  PreviousBadge,
  PublishStatusBadge,
} from './publishStatusUi';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { PublishRecord } from '@src/types.ts/publishRecord';

const DOWNLOADS_TOOLTIP_ID = 'downloads-tab-tooltip';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  adapter: Partial<IVideoCreatorStore> | null;
  videoId: string | undefined;
  videoTitle?: string;
  /**
   * Play one row's render in the preview (PLAT-11548). A succeeded row is the
   * only handle on a translated publish — it never reaches the Video record's
   * `videoFilePrimary` — so the list is also how you get to it in the player.
   */
  onPreviewRecord?: (record: PublishRecord) => void;
  /** The row whose render the preview is currently playing, if any. */
  previewingRecordId?: string | null;
}

export const DownloadsTab = observer(function ({
  videoId,
  videoTitle,
  adapter,
  onPreviewRecord,
  previewingRecordId,
}: Props) {
  const { records, loading } = useClipPublishRecords(videoId, adapter);
  const { downloadingId, download } = usePublishRecordDownload();
  // Global store (not the clip-preview adapter) — we need the full story clip
  // graph (`otherVideos`) for the share modal's download-toggle fan-out. The
  // adapter scopes renderer/timeline state to one clip but doesn't carry story
  // relationships.
  const globalVideoCreator = useVideoCreatorStore();
  const currentFingerprintFor = useCurrentPublishFingerprints(adapter);

  // Per-row Share Live Link modal — copy/open the live link for that row's
  // video + AR, and control the download permission.
  const [shareRecord, setShareRecord] = useState<PublishRecord | null>(null);

  if (loading && records.length === 0) {
    return <EmptyState>Loading...</EmptyState>;
  }

  if (!loading && records.length === 0) {
    return <EmptyState>No publishes yet for this aspect ratio.</EmptyState>;
  }

  // Only the newest up-to-date render per language + AR + resolution shows the
  // green "Current" badge; older identical renders go bare so the column
  // doesn't stack duplicate badges. `records` is newest-first (see docstring),
  // so the first up-to-date match per group wins. Mutated during the render
  // map below, which iterates in that same order.
  const currentBadgeKeys = new Set<string>();

  // A single-language story repeats the same caption on every row, so the
  // column distinguishes nothing — mute it to match the AR/resolution meta and
  // let the clip's real differences (AR, resolution, time) carry the row.
  const singleLanguage = new Set(records.map(r => r.language)).size <= 1;

  return (
    <Container>
      <HeaderRow>
        <span>
          {records.length} publish{records.length !== 1 ? 'es' : ''}
        </span>
      </HeaderRow>
      <List>
        {records.map(record => {
          const succeeded = record.publishStatus === PublishStatus.Succeeded;
          const current = succeeded
            ? currentFingerprintFor(record.language)
            : null;
          // Only claim a state we actually know: records with no stored
          // fingerprint are "unknown" (isOutdated returns false for them), so
          // they must be neither "Current" nor "Previous".
          const known = !!current && !!record.fingerprintsJson;
          const outdated = known && isOutdated(record, current);
          // Up to date, but only the newest render of each identical group is
          // badged "Current"; earlier duplicates fall through to bare.
          let isCurrent = false;
          if (known && !outdated) {
            const key = `${record.language}|${record.aspectRatio}|${record.resolution}`;
            if (!currentBadgeKeys.has(key)) {
              currentBadgeKeys.add(key);
              isCurrent = true;
            }
          }
          // Only a succeeded render with an artifact can be played.
          const previewable =
            succeeded &&
            !!record.outputJson?.video?.fileKey &&
            !!onPreviewRecord;
          return (
            <Row
              key={record.id}
              $clickable={previewable}
              $selected={previewingRecordId === record.id}
              onClick={
                previewable ? () => onPreviewRecord?.(record) : undefined
              }
              // Rows are a list of one-click choices, so each is its own button
              // rather than the list being a listbox — Enter/Space activate.
              role={previewable ? 'button' : undefined}
              tabIndex={previewable ? 0 : undefined}
              onKeyDown={
                previewable
                  ? e => {
                      // The row's own keys only — a key on an action button
                      // inside it bubbles here, and `preventDefault()` would
                      // cancel that button's own activation click.
                      if (e.target !== e.currentTarget) return;
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      onPreviewRecord?.(record);
                    }
                  : undefined
              }
              data-tooltip-id={previewable ? DOWNLOADS_TOOLTIP_ID : undefined}
              data-tooltip-content={
                previewable ? 'Play this version in the preview' : undefined
              }
            >
              <Language $muted={singleLanguage}>
                {formatLanguageCaption(record.language)}
              </Language>
              <Meta>
                <span>{record.aspectRatio}</span>
                <span>{formatResolutionShortLabel(record.resolution)}</span>
              </Meta>
              <Fill>
                {record.publishStatus === PublishStatus.Failed &&
                  record.errorMessage && (
                    <ErrorText title={record.errorMessage}>
                      {record.errorMessage}
                    </ErrorText>
                  )}
              </Fill>
              <PublishMeta>
                {record.publishedAt && (
                  // Compact stamp (e.g. 07/16/26, 4:49 PM) — the 2-digit year
                  // keeps the row narrow while still carrying the publish time.
                  <Timestamp>
                    {formatDateTime(record.publishedAt, { year: '2-digit' })}
                  </Timestamp>
                )}
                {record.publishedBy && (
                  <Publisher>{record.publishedBy}</Publisher>
                )}
              </PublishMeta>
              <ActionCell>
                {record.publishStatus === PublishStatus.Succeeded ? (
                  <ActionRow>
                    <ActionIconButton
                      onClick={e => {
                        e.stopPropagation();
                        setShareRecord(record);
                      }}
                      data-tooltip-id={DOWNLOADS_TOOLTIP_ID}
                      data-tooltip-content="Share Live Link"
                    >
                      <GlobeExternalIcon
                        width="16"
                        height="16"
                        strokeColor="currentColor"
                      />
                    </ActionIconButton>
                    <ActionIconButton
                      onClick={e => {
                        e.stopPropagation();
                        void download(record, videoTitle || videoId);
                      }}
                      disabled={downloadingId === record.id}
                      data-tooltip-id={DOWNLOADS_TOOLTIP_ID}
                      data-tooltip-content="Download"
                    >
                      <DownloadIcon
                        width="16"
                        height="16"
                        strokeColor="currentColor"
                      />
                    </ActionIconButton>
                  </ActionRow>
                ) : (
                  <PublishStatusBadge status={record.publishStatus} />
                )}
                {isCurrent && <CurrentBadge tooltipId={DOWNLOADS_TOOLTIP_ID} />}
                {outdated && current && (
                  <PreviousBadge
                    reasons={whyOutdated(record, current)}
                    tooltipId={DOWNLOADS_TOOLTIP_ID}
                  />
                )}
              </ActionCell>
            </Row>
          );
        })}
      </List>
      <Tooltip
        id={DOWNLOADS_TOOLTIP_ID}
        style={MODAL_TOOLTIP_STYLE}
        place="top"
        positionStrategy="fixed"
      />
      {shareRecord &&
        (() => {
          const { clip, mainClip } = findClipForVideoId(
            globalVideoCreator.story?.otherVideos,
            shareRecord.videoId,
          );
          return (
            <ClipShareUnlistedLinkModal
              clip={clip}
              mainClip={mainClip}
              dimension={shareRecord.aspectRatio}
              language={shareLanguageForRecord(
                shareRecord.language,
                globalVideoCreator.story?.originalVideoLanguage,
              )}
              onClose={() => setShareRecord(null)}
            />
          );
        })()}
    </Container>
  );
});

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Matches the rows' inset so the count label lines up with their content. */
  padding-inline: var(--spacing-md);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $clickable?: boolean; $selected?: boolean }>`
  display: flex;
  align-items: center;
  /* xs, not md: with the fixed language column + meta + timestamp + the
     fixed-width status cell, wider gaps overflow the pane — the badge clips
     and timestamps ellipsize. */
  gap: var(--spacing-xs);
  /* 10px, not --spacing-sm: the hover/selected fill wraps the row, and 8px sat
     tight against the two-line language cell. Inline padding is on every row,
     not just the clickable ones, so the columns sit at the same x throughout.
     It must be real padding rather than a negative margin bleeding into the
     pane: the tab panel (Tabs.tsx TabContent) has no horizontal padding and
     its overflow-y:auto makes it a scroll port on both axes, so an outward
     bleed is clipped on the left (inline-start overflow is unreachable) and
     spills past the pane on the right. */
  padding: 10px var(--spacing-md);
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      /* Deliberately NO border-radius: selectedRowIndicator draws its gold
         bar as an inset box-shadow, which a radius clips into two arcs at the
         corners — reading as two stray dots rather than one bar. */

      /* Same pairing FolderRow uses: neutral --surface on hover, and the
         elevated fill stays put under a selected row so hovering it doesn't
         read as a downgrade. */
      &:hover {
        background: var(--surface);
      }

      &:focus-visible {
        outline: 2px solid var(--accent);
        outline-offset: -2px;
      }
    `}

  /* Selection is gold and left-anchored per the design system — never green,
     and distinct from the neutral grey hover above. Placed after the hover
     block so it also wins on hover. */
  ${({ $selected }) => $selected && selectedRowIndicator}
`;

// Flexible middle slot — spaces the row and carries a failed row's error text.
const Fill = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
`;

// The single status/action column: a status badge while in flight / failed,
// the open-live-link + download actions once succeeded. Fixed width (sized to
// the widest badge, "● Publishing" / "Retry Pending") so the timestamp column
// to its left sits at the same x on every row instead of shifting between
// badge rows and the narrower icon-pair rows. Stacks the staleness pill
// ("Current" / "Previous") above the icon row (right-aligned) for a succeeded
// render.
const ActionCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: var(--spacing-2xs);
  flex: 0 0 86px;
  /* Reserve the staleness-pill line on every row (24px icon row + 2px gap +
     18px pill line) so a badged row ("Current" / "Previous") and a bare row
     share the same height — the pill only renders on some rows. */
  min-height: 44px;
`;

// The open-live-link + download icon pair, kept on one line below the pill.
const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-2xs);
`;

// Fixed-width column: language names vary a lot ("English" vs
// "Dutch (Netherlands)"), and an auto-width column pushes the AR/resolution
// column to a different x per row — the rows read misaligned. Long names wrap
// within the fixed box instead. `$muted` on a single-language story: the
// caption is the same on every row, so it drops back to the meta colour.
const Language = styled.span<{ $muted: boolean }>`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.3;
  color: ${({ $muted }) =>
    $muted ? 'var(--text-muted)' : 'var(--foreground)'};
  flex: 0 0 80px;
`;

const Meta = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 var(--spacing-xs);
  flex: 0;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 1.3;
  color: var(--text-muted);

  /* Each part (AR / resolution label) is atomic: "1080p" must not split across
     lines — wrapping happens only between the two spans. The short label
     ("270p", not "Low (270p)") is what keeps the row's timestamp from
     ellipsizing in the 380px pane; see the Row padding note. */
  & > span {
    white-space: nowrap;
  }
`;

const ErrorText = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--destructive);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

/* The one column allowed to give way when the row runs out of width — the
   status/action cell must never get clipped, so this shrinks and its lines
   ellipsize instead. */
const PublishMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  min-width: 0;
`;

const Timestamp = styled.span`
  max-width: 100%;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 1.3;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Publisher = styled.span`
  max-width: 100%;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: 1.3;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--spacing-xs);
  border-radius: 6px;
  cursor: pointer;
  /* Full-strength: these are the row's only actions, so they read as the
     primary thing in it rather than as muted meta. */
  color: var(--foreground);
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background-color: var(--surface-elevated);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xl) 0;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;
